import React, { useState, useEffect, useRef } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { PROVINCE_MAPPING } from '../../constants/clientConstants';

// Address Autocomplete component using Google Places API
export const AddressAutocomplete = ({ onPlaceSelect, placeholder = "Cerca indirizzo...", defaultValue = "", value = "" }) => {
  const inputRef = useRef(null);
  const [autocomplete, setAutocomplete] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [_isSelecting, setIsSelecting] = useState(false);
  const [inputValue, setInputValue] = useState(value || defaultValue || '');
  const places = useMapsLibrary('places');

  useEffect(() => {
    if (!places || !inputRef.current) return;

    const options = {
      fields: ['address_components', 'formatted_address', 'geometry'],
      componentRestrictions: { country: 'it' }, // Restrict to Italy
      types: ['address']
    };

    const autoCompleteInstance = new places.Autocomplete(inputRef.current, options);
    setAutocomplete(autoCompleteInstance);

    // Set default value if provided
    if (defaultValue && inputRef.current) {
      inputRef.current.value = defaultValue;
    }

    // CRITICAL: Prevent pac-container clicks from closing modal
    const preventModalClose = (e) => {
      e.stopPropagation();
      e.stopImmediatePropagation();
      // DO NOT call e.preventDefault() - it blocks Google Places API from processing click selections
      setIsSelecting(true);
      setTimeout(() => setIsSelecting(false), 500);
    };

    // Fix z-index and attach event blockers to pac-container
    const fixPacContainer = () => {
      const pacContainers = document.querySelectorAll('.pac-container');
      pacContainers.forEach(container => {
        container.style.zIndex = '99999';
        container.style.pointerEvents = 'auto';
        container.removeEventListener('mousedown', preventModalClose, true);
        container.removeEventListener('click', preventModalClose, true);
        container.removeEventListener('touchstart', preventModalClose, true);
        container.removeEventListener('pointerdown', preventModalClose, true);
        container.addEventListener('mousedown', preventModalClose, true);
        container.addEventListener('click', preventModalClose, true);
        container.addEventListener('touchstart', preventModalClose, true);
        container.addEventListener('pointerdown', preventModalClose, true);
      });
    };

    const handleFocus = () => {
      setTimeout(fixPacContainer, 50);
      setTimeout(fixPacContainer, 200);
      setTimeout(fixPacContainer, 500);
    };

    const handleInput = () => {
      setTimeout(fixPacContainer, 50);
      setTimeout(fixPacContainer, 200);
    };

    inputRef.current.addEventListener('focus', handleFocus);
    inputRef.current.addEventListener('input', handleInput);

    setTimeout(fixPacContainer, 300);

    return () => {
      if (window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autoCompleteInstance);
      }
      if (inputRef.current) {
        inputRef.current.removeEventListener('focus', handleFocus);
        inputRef.current.removeEventListener('input', handleInput);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [places, defaultValue]);

  useEffect(() => {
    if (!autocomplete) return;

    const listener = autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      console.log('Google Places - place_changed triggered:', place);

      if (place && place.address_components) {
        const addressData = {
          address: '',
          address_number: '',
          city: '',
          city_provincia: '',
          postal_code: '',
          latitude: null,
          longitude: null
        };

        if (place.geometry && place.geometry.location) {
          addressData.latitude = place.geometry.location.lat();
          addressData.longitude = place.geometry.location.lng();
        }

        const route = place.address_components.find(c => c.types.includes('route'));
        if (route) addressData.address = route.long_name;

        const streetNumber = place.address_components.find(c => c.types.includes('street_number'));
        if (streetNumber) addressData.address_number = streetNumber.long_name;

        const locality = place.address_components.find(c => c.types.includes('locality'));
        const adminArea3 = place.address_components.find(c => c.types.includes('administrative_area_level_3'));
        if (locality) addressData.city = locality.long_name;
        else if (adminArea3) addressData.city = adminArea3.long_name;

        const province = place.address_components.find(c => c.types.includes('administrative_area_level_2'));
        if (province) {
          if (province.short_name && province.short_name.length === 2) {
            addressData.city_provincia = province.short_name.toUpperCase();
          } else {
            const mapped = PROVINCE_MAPPING[province.long_name] || PROVINCE_MAPPING[province.short_name];
            addressData.city_provincia = mapped || province.short_name || '';
          }
        }

        const postalCode = place.address_components.find(c => c.types.includes('postal_code'));
        if (postalCode) addressData.postal_code = postalCode.long_name;

        const streetOnly = addressData.address || '';
        setInputValue(streetOnly);
        if (inputRef.current) inputRef.current.value = streetOnly;

        if (onPlaceSelect) onPlaceSelect(addressData);
      } else {
        if (inputRef.current && onPlaceSelect) {
          setInputValue(inputRef.current.value);
          onPlaceSelect({
            address: inputRef.current.value,
            address_number: '',
            city: '',
            city_provincia: '',
            postal_code: ''
          });
        }
      }
    });

    return () => {
      if (window.google?.maps?.event) {
        window.google.maps.event.removeListener(listener);
      }
    };
  }, [autocomplete, onPlaceSelect]);

  // Sync external value prop changes
  useEffect(() => {
    if (value && value !== inputValue) {
      setInputValue(value);
      if (inputRef.current) inputRef.current.value = value;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') e.preventDefault();
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  return (
    <input
      ref={inputRef}
      type="text"
      value={inputValue}
      onChange={handleInputChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      data-testid="address-autocomplete"
    />
  );
};

export default AddressAutocomplete;
